import { roundMoney } from "./money-math";
import type { Account, Transaction } from "../types";
import { addMonths, format, parse } from "date-fns";
// Shared categorical palette (category charts, payee avatars) so anything colored by a stable
// key looks consistent across the app.
export const palette = [
  "#b9ef99",
  "#a6bafc",
  "#f2c885",
  "#e9a8bd",
  "#79c7be",
  "#c4a9e9",
  "#c7cb92",
  "#96b6c8",
];
export const isExpense = (t: Transaction) => t.type.toLowerCase() === "expense";
export const isIncome = (t: Transaction) => t.type.toLowerCase() === "income";
// Loan legs and confirmed currency conversions remain real account movements.
// All spending/income reports use these predicates; cash and goal purchases use the raw types.
export const isSpendable = (t: Transaction) => isExpense(t) && !t.iouId && !t.currencyConversion;
export const isEarnable = (t: Transaction) => isIncome(t) && !t.iouId && !t.currencyConversion;
export const isCard = (a: Account) => /credit/i.test(a.type);
// A bill's next due date after `date`. The server's bill writes use this same function, so the
// preview the app shows before saving is the date that gets stored. Unknown rules step monthly.
export function advanceDueDate(date: string, rule: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (rule === "weekly") d.setUTCDate(d.getUTCDate() + 7);
  else if (rule === "yearly") d.setUTCFullYear(d.getUTCFullYear() + 1);
  else d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 10);
}
export const reportableBase = (t: {
  amountBase: number | null;
  reportableAmountBase?: number | null;
}) => t.reportableAmountBase ?? t.amountBase;
export const total = (
  items: { amountBase: number | null; reportableAmountBase?: number | null }[],
) =>
  items.some((t) => reportableBase(t) === null)
    ? null
    : items.reduce((s, t) => s + (reportableBase(t) ?? 0), 0);
// Building a NumberFormat is far costlier than using one, and `money` runs for every row rendered.
const numberFormats = new Map<number, Intl.NumberFormat>();
const numberFormat = (digits: number) => {
  let format = numberFormats.get(digits);
  if (!format) {
    format = new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
    numberFormats.set(digits, format);
  }
  return format;
};
export const money = (value: number | null, currency = "MVR", digits = 2) =>
  value === null
    ? "Unavailable"
    : numberFormat(digits).format(value) + (currency === "USD" ? " USD" : ` ${currency}`);
/** Currency fee included in `amount` when `markup` is a positive percentage (e.g. "3.50%" or "15"). */
export function markupAmount(amount: number, markup?: string): number | null {
  const pct = Number(markup?.replace(/%$/, "").trim());
  if (!Number.isFinite(pct) || pct <= 0 || !Number.isFinite(amount) || amount <= 0) return null;
  const base = roundMoney(amount / (1 + pct / 100));
  const fee = roundMoney(amount - base);
  return fee > 0 ? fee : null;
}
export const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;
export const shortName = (name: string) =>
  name.replace(/\[[^\]]+\]\s*/g, "").replace(/ Account$/i, "");
export const dateLabel = (date: string) =>
  format(new Date(date.slice(0, 10) + "T12:00:00"), "d MMM yyyy").replace(
    "Sep",
    "Sept",
  );
// `date` is a plain calendar date the person can edit; `createdAt` is a real instant (when the row
// was actually written) and needs an actual time-of-day, shown in the household's own timezone
// rather than the viewer's.
export const dateTimeLabel = (iso: string, timezone = "Indian/Maldives") => {
  const d = new Date(iso);
  const datePart = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    day: "numeric",
    month: "short",
    year: "numeric",
  })
    .format(d)
    .replace(/\bSep\b/, "Sept");
  const timePart = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return `${datePart}, ${timePart}`;
};
// One formatter per timezone: `todayIn` runs on every render and every AI request.
const dayFormats = new Map<string, Intl.DateTimeFormat>();
export const todayIn = (timezone = "Indian/Maldives") => {
  let format = dayFormats.get(timezone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" });
    dayFormats.set(timezone, format);
  }
  return format.format(new Date());
};
/** "HH:MM", 24-hour, in the household's own timezone rather than the device's — the field this
 * fills is an optional annotation of when the money moved, not a record of where the phone was. */
export const nowTimeIn = (timezone = "Indian/Maldives") =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date());
export const parseDate = (input: string): string => {
  try {
    // Try parsing "10 Sep 2020" format ("Sept" from dateLabel round-trips the same way)
    const parsed = parse(input.replace("Sept", "Sep"), "d MMM yyyy", new Date());
    if (!isNaN(parsed.getTime())) {
      return format(parsed, "yyyy-MM-dd");
    }
  } catch {
    // Fall through to next attempt
  }
  // Try parsing ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }
  // Return as-is if can't parse
  return input;
};

export type CardStatementStatus = {
  state: "missing" | "stale" | "current";
  missing: string[];
  repayments: number;
  remaining: number | null;
  overdue: boolean;
};

export const cardStatementStatus = (
  account: Account,
  transactions: Transaction[],
  today = todayIn(),
): CardStatementStatus => {
  const missing = [
    ["Credit limit", account.creditLimit],
    ["Statement recorded date", account.statementRecordedDate],
    ["Statement balance", account.statementBalance],
    ["Minimum payment", account.minimumPayment],
    ["Statement due date", account.statementDueDate],
  ].filter(([, value]) => value === null || value === undefined || value === "").map(([label]) => String(label));
  if (missing.length) return { state: "missing", missing, repayments: 0, remaining: null, overdue: false };
  const repayments = transactions
    .filter((transaction) => transaction.toAccountId === account.id && transaction.date.slice(0, 10) >= account.statementRecordedDate! && transaction.date.slice(0, 10) <= today)
    .reduce((sum, transaction) => sum + (transaction.receivedAmount || 0), 0);
  const remaining = Math.max(0, (account.statementBalance || 0) - repayments);
  // Recording is our tracking start, not the issuer’s cycle date. Remind after
  // one calendar month, and never before the saved payment deadline.
  const reviewDate = format(addMonths(parse(account.statementRecordedDate!, "yyyy-MM-dd", new Date()), 1), "yyyy-MM-dd");
  return {
    state: today >= reviewDate && today > account.statementDueDate! ? "stale" : "current",
    missing: [],
    repayments,
    remaining,
    overdue: remaining > 0 && today > account.statementDueDate!,
  };
};

/** Summary line for a transfer preview (target account name, converted amount and rate). */
export function transferSummary(
  expense: { amount: string | number; exchangeRate?: string | number; toAccountId?: string },
  accounts: { id: string; name: string; currency?: string }[] = [],
): string {
  const toAccount = accounts.find((a) => a.id === expense.toAccountId);
  const targetName = shortName(toAccount?.name || "");
  const rate = Number(expense.exchangeRate || 1);
  const received = roundMoney(Number(expense.amount) * rate);
  const currency = toAccount?.currency || "MVR";
  return `To ${targetName} · Receives ${money(received, currency)} · Rate ${expense.exchangeRate || rate}`;
}
